import { useEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import {
  Router as ReactLocationRouter,
  Outlet as LegacyOutlet,
  createBrowserHistory,
  ReactLocation,
  useRouter as useLegacyRouter,
  useNavigate as useLegacyNavigate,
} from '@tanstack/react-location';
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools';
import { hasACL, CoreACLs, type PortalUser, setLastVisitedPage } from '@frontend/auth-helpers';
import { useRemoveAppLoader } from '@frontend/document';
import env from '@frontend/env';
import { ErrorBoundary, MainBoundary } from '@frontend/error-boundary';
import type { WeaveRoute } from '@frontend/file-based-routing';
import { INTAKE_FORM_PATH_V2 } from '@frontend/onboarding';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useScopedAppFlagStore } from '@frontend/scope';
import { shell } from '@frontend/shell-utils';
import { createContextWithSelectors, useContextSelector } from '@frontend/store';
import { Outlet, useRouter, router, RootRoute } from '@frontend/weave-router';
import { AuthenticatedApp } from '../components/authenticated-app';
import { EnvironmentSwitcher } from '../components/environment-switcher';
import { GlobalSearchView } from '../components/global-search';
import { HistoryControl } from '../components/history-control/history-control';
import { useDocumentTitle } from '../components/use-document-title';
import { VersionInfo } from '../components/version-info';
import { useApp } from '../providers/app.provider';
import { useRoutes } from '../providers/routes.provider';
import { useThirdPartyScripts } from '../third-party-scripts/use-third-party-scripts';

const history = createBrowserHistory();
export const location = new ReactLocation({
  history,
});

const signInUri = '/sign-in';
export function RootRouteComponent() {
  useRemoveAppLoader();

  const { routes: legacyRoutes } = useRoutes();
  const data = RootRoute.useRouteContext();
  const hasShellThemeEnabled = shell.isShell && shell.featureAvailability?.has('shell-theme');
  const showEnvSwitch = hasACL('weave', CoreACLs.DEVTOOLS) || import.meta.env.MODE === 'development';
  const originalURI = window.location.pathname + window.location.search;
  if (originalURI && !originalURI.startsWith(signInUri)) {
    setLastVisitedPage(originalURI);
  }
  useThirdPartyScripts();
  return (
    <>
      <VersionInfo />
      <EnvironmentSwitcher enabled={showEnvSwitch} />

      <ReactLocationRouter
        basepath={env.BASE_PATH}
        routes={legacyRoutes as WeaveRoute[]}
        location={location}
        useErrorBoundary={false}
      >
        <LegacyRouterProvider>
          <>
            <LegacyRouterSync />
            <ErrorBoundary fallback={MainBoundary}>
              <AppComponent authenticated={data.authenticated} user={data.user}>
                <Outlet />
              </AppComponent>
            </ErrorBoundary>
          </>
        </LegacyRouterProvider>
        {hasShellThemeEnabled && <TitleBarNavigationControls />}
        {hasShellThemeEnabled && <TitleBarGlobalSearch />}
        {(import.meta.env.MODE === 'development' || localStorage.getItem('debug.show-router-devtools')) && (
          <TanStackRouterDevtools />
        )}
      </ReactLocationRouter>
    </>
  );
}

type ContextValue = {
  isPublic: boolean;
};
/**
 * Just provide primitive access to whatever variables needed in sync mechanisms to reduce high level re-renders
 */
const LegacyRouterContext = createContextWithSelectors<ContextValue>(null as any);
const LegacyRouterProvider = ({ children }: { children: ReactNode }) => {
  const isPublicPrev = useRef(false);
  const newRouter = useRouter();
  const legacyRouter = useLegacyRouter();
  const matches = newRouter.state.matches;
  const deepestMatch = matches?.at(-1);

  const legacyStateMatch = legacyRouter.state.matches?.filter((match) => match.route.id !== '*')[0];
  const legacyRouteType = legacyStateMatch?.route?.meta?.type;

  const isNewRoute = !newRouter.hasNotFoundMatch();
  const isNewRouterPublicRoute = deepestMatch?.id.startsWith('/_public');
  const isSynced = newRouter.latestLocation.pathname === legacyRouter.state.location.pathname;

  //There are still some false positives with the public route check. This will prevent any of these pages from being false positives
  //TODO: this shouldn't be necessary, but this should hopefully completely elminate the occurrence while we work through the new router
  const hardCodedPrivateRoutePrefixes = ['/home', '/messages', '/payments', '/calls', '/forms', '/fax', '/analytics'];
  const isNotHardCodedPrivateRoute = !hardCodedPrivateRoutePrefixes.some((prefix) =>
    newRouter.latestLocation.pathname.startsWith(prefix)
  );

  const isPublicRoute =
    (isNotHardCodedPrivateRoute &&
      (isNewRoute ? isNewRouterPublicRoute : isSynced ? legacyRouteType !== 'private' : isPublicPrev.current)) ||
    newRouter.latestLocation.pathname === '/';

  isPublicPrev.current = isPublicRoute;

  const value: ContextValue = {
    isPublic: isPublicRoute,
  };
  console.log({ router, legacyStateMatch, matches: router.state.matches, current: isPublicPrev.current, isSynced });
  return <LegacyRouterContext.Provider value={value}>{children}</LegacyRouterContext.Provider>;
};

/**
 * This will be temporary. The not found route of the new router will show the legacy outlet
 */
type Okay = any;
router.update({
  defaultNotFoundComponent: () => {
    return <NotFoundComponent />;
  },
} as Okay); // Context is already defined in the new route, I don't think the react-router types have great support this, but it does work fine as far as I can tell

const NotFoundComponent = () => {
  return <LegacyOutlet />;
};
const LegacyRouterSync = () => {
  const legacyRouter = useLegacyRouter();
  const legacyNavigate = useLegacyNavigate();
  const router = useRouter();

  // Use refs to avoid stale closures in effects and for comparison
  const legacyRouterRef = useRef(legacyRouter);
  const routerRef = useRef(router);
  const legacyNavigateRef = useRef(legacyNavigate);

  // A flag to prevent the infinite loop during synchronization
  const isSyncingRef = useRef(false);

  useEffect(() => {
    legacyRouterRef.current = legacyRouter;
    routerRef.current = router;
    legacyNavigateRef.current = legacyNavigate;

    const handler = () => {
      // If we're currently in a sync operation triggered by us, don't react
      if (isSyncingRef.current) {
        return;
      }

      const newRouterLocation = routerRef.current.latestLocation;
      const legacyRouterLocation = legacyRouterRef.current.state.location;

      // Only sync if the paths are different
      if (
        newRouterLocation.pathname !== legacyRouterLocation.pathname ||
        JSON.stringify(newRouterLocation.search) !== JSON.stringify(legacyRouterLocation.search)
      ) {
        console.info(
          `Syncing legacy router location from ${legacyRouterLocation.pathname} to new router's latest location ${newRouterLocation.pathname}`
        );

        isSyncingRef.current = true; // Set the flag to true before navigating
        // Use legacyNavigate with 'replace' and then reset the flag
        legacyNavigateRef.current({
          to: newRouterLocation.pathname,
          search: newRouterLocation.search,
          hash: newRouterLocation.hash,
          replace: true, // Crucial: Replaces history entry, doesn't push
        });

        // Use finally to ensure flag is reset even on errors
        isSyncingRef.current = false; // Reset the flag after navigation completes
      }
    };

    // Subscribe to changes in the new router
    // onBeforeNavigate is a good choice as it fires before the new router's state settles
    const unsubscribe = router.subscribe('onBeforeNavigate', handler);

    // Initial sync on mount if needed (optional)
    // We might want to do an initial sync here if the new router loads
    // a different location than the legacy router at start.
    // handler(); // Uncomment if you need an initial sync

    return () => {
      unsubscribe();
    };
  }, [router, legacyRouter, legacyNavigate]); // Dependencies for the effect

  return null;
};

const AppComponent = ({
  authenticated,
  user,
  children,
}: {
  authenticated: boolean;
  user: PortalUser | undefined;
  children: React.ReactNode;
}) => {
  const pathname = router.state.location.pathname;
  const isPublicRoute = useContextSelector(LegacyRouterContext, (ctx) => ctx.isPublic);

  useDocumentTitle(pathname);
  const { updateAvailable, currentVersion, shouldPreventUpdate } = useApp();

  const isIntakeForm = pathname === INTAKE_FORM_PATH_V2;
  const isWeaveDemoUser = user?.username.toLowerCase().includes('@demoweave');

  useAppUpdater({
    history: router.history,
    updateAvailable,
    enabled: !shouldPreventUpdate && !isWeaveDemoUser,
    currentVersion,
  });

  const originalURI = window.location.pathname + window.location.search;
  const shouldRedirectToSignIn = !authenticated && !isPublicRoute && originalURI !== signInUri;
  const shouldWrapInAuthenticatedApp = authenticated && !isPublicRoute && !isIntakeForm;

  // TODO: - @gisheri - remove this when ready
  console.log({
    time: Date.now(),
    router,
    pathname,
    latestLocationPathname: router.latestLocation.pathname,
    resolvedLocationPathname: router.state.resolvedLocation?.pathname,
    shouldWrapInAuthenticatedApp,
    authenticated,
    isPublicRoute,
    isIntakeForm,
    shouldRedirectToSignIn,
  });

  if (shouldRedirectToSignIn) {
    console.info(`Hit a private route ${originalURI} without being authenticated. Redirecting to ${signInUri}`);
    window.location.pathname = signInUri;
    return 'redirecting';
  }

  if (shouldWrapInAuthenticatedApp) {
    return <AuthenticatedApp>{children}</AuthenticatedApp>;
  }

  return <>{children}</>;
};

const useAppUpdater = ({
  history,
  updateAvailable,
  enabled,
  currentVersion,
}: {
  history: typeof router.history;
  enabled: boolean;
  updateAvailable: boolean;
  currentVersion: string | undefined;
}) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    const unsubscribe = history.subscribe(() => {
      if (enabled) {
        return;
      }
      if (!enabled && updateAvailable) {
        timeoutRef.current = setTimeout(() => {
          console.info('Refreshing app because new version is available (' + currentVersion + ')');
          window.location.reload();
        }, 20);
      }
    });

    return () => {
      unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [history, enabled, updateAvailable, currentVersion]);
};

const TitleBarGlobalSearch = () => {
  const titleBarContentRef = useRef<HTMLElement | null>(null);
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const hasBubbleNavMode = getFeatureFlagValue('bubbleNavMode');
  const isXsmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const showGlobalSearch = hasBubbleNavMode && isXsmall;

  useEffect(() => {
    if (!titleBarContentRef.current) {
      const element = document.getElementById('weave-shell-title-bar-content');
      if (element) titleBarContentRef.current = element;
    }
  });

  if (!titleBarContentRef.current || showGlobalSearch) return null;
  return createPortal(<GlobalSearchView />, titleBarContentRef.current);
};

const TitleBarNavigationControls = () => {
  const titleBarNavigationRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!titleBarNavigationRef.current) {
      const element = document.getElementById('weave-shell-title-bar-buttons');
      if (element) titleBarNavigationRef.current = element;
    }
  });

  if (!titleBarNavigationRef.current) return null;
  return createPortal(<HistoryControl />, titleBarNavigationRef.current);
};
